import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { usePanelRef } from 'react-resizable-panels';

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/_shared/components/ui/resizable';
import { cn } from '@/_shared/lib/utils';
import { PREVIEW_PANEL_SLIDE_MS } from '@/chat/hooks/usePreviewPanelSlide';

const ASSISTANT_PANEL_DEFAULT_VW = 0.4;
const ASSISTANT_PANEL_DEFAULT_MAX_PX = 600;
const ASSISTANT_PANEL_MIN_PX = 280;
const ASSISTANT_PANEL_MAX_PX = 720;
/** Fallback when `window` is unavailable (SSR / non-browser test hosts). */
const ASSISTANT_PANEL_SSR_VIEWPORT_PX = 1024;
/** Bumped when default sizing changes so persisted layouts don't stick on the old width. */
const ASSISTANT_PANEL_LAYOUT_ID = 'dashboard-assistant-v2';

/** Desktop split-shell assistant region — distinct from the mobile sheet id. */
export const DASHBOARD_ASSISTANT_DESKTOP_ID = 'dashboard-chat-sidebar';
/** Mobile sheet assistant region — distinct from the desktop split-shell id. */
export const DASHBOARD_ASSISTANT_MOBILE_ID = 'dashboard-chat-sidebar-mobile';

// eslint-disable-next-line react-refresh/only-export-components -- unit-tested layout helper co-located with panel shell
export function assistantPanelTargetWidthPx(viewportWidth: number): number {
  return Math.min(viewportWidth * ASSISTANT_PANEL_DEFAULT_VW, ASSISTANT_PANEL_DEFAULT_MAX_PX);
}

function readViewportWidthPx(): number {
  return typeof window !== 'undefined' ? window.innerWidth : ASSISTANT_PANEL_SSR_VIEWPORT_PX;
}

interface DashboardAssistantSlideShellProps {
  shown: boolean;
  main: React.ReactNode;
  children: React.ReactNode;
  /** Called when the user drags the panel below minSize (library collapses to 0). */
  onClose?: () => void;
}

/** Desktop split: dashboard content + resizable assistant column with width + slide transition. */
export function DashboardAssistantSlideShell({
  shown,
  main,
  children,
  onClose,
}: DashboardAssistantSlideShellProps): React.ReactElement {
  const assistantPanelRef = usePanelRef();
  const wasShownRef = useRef(shown);
  const shownRef = useRef(shown);
  const onCloseRef = useRef(onClose);
  // Recompute when `shown` flips so a pre-open viewport resize is picked up (defaultSize is mount-time).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `shown` triggers re-read of viewport width
  const defaultWidthPx = useMemo(() => assistantPanelTargetWidthPx(readViewportWidthPx()), [shown]);

  useLayoutEffect(() => {
    shownRef.current = shown;
  }, [shown]);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // CSS alone cannot collapse a persisted resizable panel — drive it imperatively so
  // closing the assistant returns the dashboard to full width without unmounting it.
  useLayoutEffect(() => {
    const panel = assistantPanelRef.current;
    if (!panel) return undefined;

    const applyOpenSize = (): void => {
      const current = assistantPanelRef.current;
      if (!current) return;
      const target = assistantPanelTargetWidthPx(readViewportWidthPx());
      if (current.isCollapsed()) {
        // expand() restores the pre-collapse size (user drag / autoSaveId), not defaultSize.
        current.expand();
      }
      // Preserve user-dragged / persisted width on reopen. Only snap to the default when
      // expand left us below min (first open, or a poisoned collapse size).
      const { inPixels } = current.getSize();
      if (inPixels < ASSISTANT_PANEL_MIN_PX) {
        current.resize(target);
      }
    };

    if (shown) {
      applyOpenSize();
      const outerId = requestAnimationFrame(() => {
        applyOpenSize();
      });
      wasShownRef.current = shown;
      return () => cancelAnimationFrame(outerId);
    }

    if (wasShownRef.current) {
      const closeTimer = window.setTimeout(() => {
        assistantPanelRef.current?.collapse();
      }, PREVIEW_PANEL_SLIDE_MS);
      wasShownRef.current = shown;
      return () => clearTimeout(closeTimer);
    }

    wasShownRef.current = shown;
    return undefined;
  }, [shown, assistantPanelRef]);

  // collapsible + minSize: dragging below min collapses to 0. Treat that as dismiss.
  const handleAssistantResize = useCallback(
    (size: { inPixels: number }, _id: string | number | undefined, prev: { inPixels: number } | undefined) => {
      if (!shownRef.current || prev === undefined) return;
      if (size.inPixels > 0 && !assistantPanelRef.current?.isCollapsed()) return;
      onCloseRef.current?.();
    },
    [assistantPanelRef],
  );

  return (
    <ResizablePanelGroup direction="horizontal" autoSaveId={ASSISTANT_PANEL_LAYOUT_ID} className="min-h-0 flex-1">
      <ResizablePanel id="dashboard-main" minSize="25%">
        <div className="flex h-full min-h-0 flex-col overflow-hidden">{main}</div>
      </ResizablePanel>
      <ResizableHandle withHandle className={cn(!shown && 'hidden')} aria-label="Resize dashboard assistant" />
      <ResizablePanel
        id="dashboard-assistant"
        panelRef={assistantPanelRef}
        defaultSize={`${defaultWidthPx}px`}
        minSize={`${ASSISTANT_PANEL_MIN_PX}px`}
        maxSize={`${ASSISTANT_PANEL_MAX_PX}px`}
        collapsible
        collapsedSize={0}
        onResize={handleAssistantResize}
        className="overflow-hidden"
      >
        <div
          id={DASHBOARD_ASSISTANT_DESKTOP_ID}
          className={cn(
            'flex h-full w-full flex-col overflow-hidden bg-background motion-safe:transition-transform motion-safe:ease-out',
            shown ? 'translate-x-0' : 'translate-x-full',
            'motion-reduce:transition-none',
          )}
          style={{ transitionDuration: `${PREVIEW_PANEL_SLIDE_MS}ms` }}
        >
          {children}
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
